import { create, useStore } from 'zustand';

export interface BankPanelState {
  isEditingAny: boolean;
  _quotesBeingCreated: Set<string>;
  _quotesBeingEditted: Set<string>;
  setIsEditing(): void;
  setQuoteBeingCreated(loanQuoteID: string): void;
  setQuoteBeingEditted(loanQuoteID: string): void;
  setCancelEditing(loanQuoteID: string): void;
  isQuoteBeingEdited(loanQuoteID: string): boolean;
  getQuotesBeingEdited(): Set<string>;
  getQuotesBeingCreated(): Set<string>;
  setCancelAll(): void;
}

const createBankPanelStore = () =>
  create<BankPanelState>((set, get) => ({
    isEditingAny: false,
    _quotesBeingCreated: new Set<string>(),
    _quotesBeingEditted: new Set<string>(),
    isQuoteBeingEdited(loanQuoteID: string) {
      return (
        get()._quotesBeingCreated.has(loanQuoteID) ||
        get()._quotesBeingEditted.has(loanQuoteID)
      );
    },
    getQuotesBeingCreated() {
      return get()._quotesBeingCreated;
    },
    getQuotesBeingEdited() {
      return get()._quotesBeingEditted;
    },
    setIsEditing() {
      set(() => ({
        isEditingAny: true,
      }));
    },
    setQuoteBeingCreated(loanQuoteID) {
      set((state) => ({
        isEditingAny: true,
        _quotesBeingCreated: new Set(state._quotesBeingCreated).add(
          loanQuoteID
        ),
      }));
    },
    setQuoteBeingEditted(loanQuoteID) {
      set((state) => ({
        _quotesBeingEditted: new Set(state._quotesBeingEditted).add(
          loanQuoteID
        ),
      }));
    },
    setCancelEditing(loanQuoteID) {
      set((state) => {
        const newQuotesBeingCreated = new Set(state._quotesBeingCreated);
        const newQuotesBeingEditted = new Set(state._quotesBeingEditted);
        newQuotesBeingCreated.delete(loanQuoteID);
        newQuotesBeingEditted.delete(loanQuoteID);
        return {
          _quotesBeingCreated: newQuotesBeingCreated,
          _quotesBeingEditted: newQuotesBeingEditted,
          isEditingAny:
            newQuotesBeingCreated.size > 0 || newQuotesBeingEditted.size > 0,
        };
      });
    },
    setCancelAll() {
      set(() => ({
        isEditingAny: false,
        _quotesBeingCreated: new Set(),
        _quotesBeingEditted: new Set(),
      }));
    },
  }));

export const quoteStores: Record<
  string,
  ReturnType<typeof createBankPanelStore>
> = {};

export function getOrSetQuoteStore(applicationId: string) {
  if (!quoteStores[applicationId])
    quoteStores[applicationId] = createBankPanelStore();

  return quoteStores[applicationId];
}

export function useBankPanelStore(applicationId: string) {
  return useStore(getOrSetQuoteStore(applicationId));
}
